import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiClient } from "../lib/api-client";
import { queryClient } from "../lib/query-client";
import { useToast } from "./useToast";
import { useNotificationStore } from "../stores/notification-store";
import type { ApiResponse } from "../types/auth";
import type {
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
} from "../types/notification";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (params?: NotificationQueryParams) =>
    ["notifications", "list", params] as const,
  unreadCount: () => ["notifications", "unread-count"] as const,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NotificationQueryParams {
  page?: number;
  limit?: number;
  is_read?: boolean;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useNotifications(params: NotificationQueryParams = {}) {
  const setRecentNotifications = useNotificationStore(
    (s) => s.setRecentNotifications
  );
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);

  const query = useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams();
      if (params.page != null) qs.set("page", String(params.page));
      if (params.limit != null) qs.set("limit", String(params.limit));
      if (params.is_read != null) qs.set("is_read", String(params.is_read));

      const queryString = qs.toString();
      const url = queryString
        ? `/notifications?${queryString}`
        : "/notifications";

      const res = await apiClient.get<ApiResponse<NotificationListResponse>>(
        url,
        { signal }
      );
      return res.data;
    },
    staleTime: 0,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Sync to Zustand store when data arrives
  useEffect(() => {
    if (query.data) {
      setRecentNotifications(query.data.items ?? []);
      setUnreadCount(query.data.unread_count ?? 0);
    }
  }, [query.data, setRecentNotifications, setUnreadCount]);

  return query;
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async ({ signal }) => {
      const res = await apiClient.get<ApiResponse<UnreadCountResponse>>(
        "/notifications/unread-count",
        { signal }
      );
      return res.data;
    },
    // Poll every 60s as a fallback to SSE
    refetchInterval: 60_000,
    staleTime: 30_000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export function useMarkNotificationRead() {
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.patch<ApiResponse<Notification>>(`/notifications/${id}/read`),
    onSuccess: (_, id) => {
      // Optimistically already done — also sync via store
      markAsRead(id);
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: () => {
      toast.error("Failed to mark notification as read");
    },
  });
}

export function useMarkAllNotificationsRead() {
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const toast = useToast();

  return useMutation({
    mutationFn: () =>
      apiClient.patch<ApiResponse<{ updated_count: number }>>(
        "/notifications/read-all"
      ),
    onSuccess: () => {
      markAllAsRead();
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: () => {
      toast.error("Failed to mark all notifications as read");
    },
  });
}

export function useDeleteNotification() {
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onError: () => {
      toast.error("Failed to delete notification");
    },
  });
}
